// THE single source of truth for "does a quote line have the artwork it needs?"
// shared verbatim by every surface that judges upload readiness:
//   - the portal pre-flight before "See my price" (QuoteEdit.collectConfigMisses),
//   - the portal "Save" report (same collector),
//   - the authoritative backend price gate (services/pricing.priceQuote), AND
//   - the submit gate (storage/assets.assertQuoteUploadsReady) — which already
//     calls validateRoleSet directly.
//
// This is the upload-axis counterpart to lineConfigIssues (form-validation.ts):
// that validator skips file_reference fields (uploads don't live in `config`);
// THIS one judges the Asset upload state. Reusing validateRoleSet — the SAME
// family→required-roles rule the submit gate uses — is what keeps price and
// submit from ever disagreeing about which files a job needs (ADR-011): a quote
// can no longer be priceable yet rejected at submit for a missing cover/manuscript.
//
// Scope: this enforces the portal-mastered FAMILY_COMPONENT_ROLES requirement
// (cover + guts for books, a single artwork for flat work, with the "one
// combined PDF satisfies cover + guts" escape hatch). It does NOT price.
import { ALL_COMPONENT_ROLES, FAMILY_COMPONENT_ROLES, familyRequiredRoles, validateRoleSet, } from '../types/asset.js';
import { customPackagingCarriesArt, mediaPackagingCarriesArt } from '../vocabulary.js';
import { readLineProductType } from './product-type.js';
// "Quote now, art to follow", recorded PER ARTWORK SLOT on the line it belongs
// to: the component roles the customer ticked "I don't have my final artwork
// yet" on. Per slot, not per quote, because a job may print only the guts, or
// arrive with half its art — a ticked cover says nothing about the manuscript.
// Lives in `QuoteLine.config` so it persists, clones and round-trips with the
// rest of the line (R26); it is stripped from the forwarded product config and
// crosses instead as the line's `artwork_status` (lineArtworkStatus below).
export const ARTWORK_PENDING_ROLES_CONFIG_KEY = 'artwork_pending_roles';
function configRecord(config) {
    return (typeof config === 'object' && config !== null && !Array.isArray(config) ? config : {});
}
// The line-config flags validateRoleSet judges a line's artwork by. Exported so
// the submit gate reads the same four the price gate and the SPA do.
export function lineArtworkFlags(config) {
    const record = configRecord(config);
    return {
        self_cover: record.self_cover === true,
        combined_artwork: record.combined_artwork === true,
        packaging_art: mediaPackagingCarriesArt(record),
        custom_packaging_art: customPackagingCarriesArt(record),
    };
}
// The slots a line can mark as art-to-follow, in family order: the artwork the
// family requires under the line's own escape hatches (cover + guts, guts alone
// for a self-cover booklet, one `combined` file for a collapsed pair, `general`
// for flat work). A family that requires no file but offers the `combined`
// catch-all (media) gets that one slot, so "send it later" still has a home.
// Empty for a half-started line and for a family with no artwork at all.
export function linePendableArtworkRoles(config) {
    const record = configRecord(config);
    const productType = readLineProductType(config);
    if (!productType)
        return [];
    const required = [...familyRequiredRoles(productType, lineArtworkFlags(record))];
    if (required.length > 0)
        return required;
    const specs = Object.hasOwn(FAMILY_COMPONENT_ROLES, productType)
        ? FAMILY_COMPONENT_ROLES[productType]
        : [];
    return specs.some((s) => s.role === 'combined') ? ['combined'] : [];
}
// The roles this line has marked art-to-follow: the stored list, read through
// the closed role vocabulary and narrowed to the slots the line can currently
// mark. A role left behind by a family switch or a toggled escape hatch names a
// slot that is no longer on the page, so it drops out here rather than waiving
// or reporting anything.
export function linePendingArtworkRoles(config) {
    const raw = configRecord(config)[ARTWORK_PENDING_ROLES_CONFIG_KEY];
    if (!Array.isArray(raw))
        return new Set();
    const stored = new Set(raw.filter((r) => ALL_COMPONENT_ROLES.includes(r)));
    return new Set(linePendableArtworkRoles(config).filter((r) => stored.has(r)));
}
// The line config with one slot's art-to-follow mark set or cleared. Writes the
// normalized list and drops the key once it is empty, so an untouched line
// carries nothing.
export function withArtworkPendingRole(config, role, pending) {
    const next = new Set(linePendingArtworkRoles(config));
    if (pending)
        next.add(role);
    else
        next.delete(role);
    const ordered = linePendableArtworkRoles(config).filter((r) => next.has(r));
    const { [ARTWORK_PENDING_ROLES_CONFIG_KEY]: _dropped, ...rest } = config;
    return ordered.length > 0 ? { ...rest, [ARTWORK_PENDING_ROLES_CONFIG_KEY]: ordered } : rest;
}
// The pendable slots whose file is actually in `roles` — a role uploaded
// directly, or cover + guts satisfied by a booklet's `combined` file. The SPA
// clears a slot's art-to-follow mark through this the moment its file lands.
export function lineUploadedArtworkRoles(config, roles) {
    const productType = readLineProductType(config);
    if (!productType)
        return new Set();
    const { missing } = validateRoleSet(productType, roles, lineArtworkFlags(configRecord(config)));
    const required = familyRequiredRoles(productType, lineArtworkFlags(configRecord(config)));
    return new Set(linePendableArtworkRoles(config).filter((r) => required.has(r) ? !missing.includes(r) : roles.has(r)));
}
// The line config with every mark removed whose slot's file is on this line.
// The file arriving answers the question, so a mark can never outlive it: the
// SPA applies this as the file lands, and the save seam applies it again so a
// mark saved before the upload is never stored beside the file.
export function withoutLandedArtworkMarks(config, lineRoles) {
    const pending = linePendingArtworkRoles(config);
    if (pending.size === 0)
        return config;
    const landed = lineUploadedArtworkRoles(config, lineRoles);
    let next = config;
    for (const role of pending) {
        if (landed.has(role))
            next = withArtworkPendingRole(next, role, false);
    }
    return next;
}
// Per-slot artwork state for one line, as it crosses to Nexus on the pricing
// projects[] and the /quote-detail lines[] (and on submit by reference, inside
// quote_token). One entry per pendable slot, each judged on its own, so Nexus
// can tell "nothing supplied" from "half supplied". Nexus owns what follows from
// it — Hands-on Prep applies when EVERY entry is `pending`; the portal decides
// nothing from the set as a whole.
//
//   uploaded — the file is in hand: on this line, or pooled quote-wide as every
//              upload gate reads it
//   pending  — the customer ticked "I don't have my final artwork yet" here
//   missing  — neither; the price and submit gates refuse this, so it reaches
//              only the import feed, for a quote a rep pulls before it is ready
//
// A file on THIS line beats a mark on it, the same rule the SPA applies when it
// clears the mark as the file lands — so a mark saved before the upload can
// never report art-to-follow for a slot whose file is here. A file pooled from
// another line does not: the customer's mark says this line's own art is still
// to come.
export function lineArtworkStatus(config, presentRoles, lineRoles) {
    const pending = linePendingArtworkRoles(config);
    const onLine = lineUploadedArtworkRoles(config, lineRoles);
    const pooled = lineUploadedArtworkRoles(config, presentRoles);
    return linePendableArtworkRoles(config).map((role) => ({
        component_role: role,
        status: onLine.has(role)
            ? 'uploaded'
            : pending.has(role)
                ? 'pending'
                : pooled.has(role)
                    ? 'uploaded'
                    : 'missing',
    }));
}
// Whether any slot on any line is marked art-to-follow — the quote-level
// `Quote.artwork_pending` Nexus stamps on the Opportunity and rides the "subject
// to change upon receipt of final artwork" disclaimer from. A pure function of
// the stored line configs, recomputed at every write of the line set (the
// proof_type pattern), never read off anything that moves.
export function quoteArtworkPending(lineConfigs) {
    return lineConfigs.some((c) => linePendingArtworkRoles(c).size > 0);
}
// Inverse of the portal renderer's FILE_REFERENCE_ROLE map
// (packages/portal/src/components/SchemaForm.tsx), restricted to the
// family-REQUIRED roles — the only roles validateRoleSet can report as missing
// (`general` for flat work, `cover` / `guts` for books). It maps each required
// role back to the file_reference field the customer uploads through, so a
// missing upload surfaces through the SAME field-keyed UX as a missing config
// field (inline error + near-button summary + click-to-scroll to the slot).
//
// Keep in lockstep with FILE_REFERENCE_ROLE: if a field key there changes for
// one of these roles, change it here too. (component_role is portal-mastered
// vocabulary; this inverse lives in shared because both the SPA pre-flight and
// the backend price gate need it.)
export const REQUIRED_UPLOAD_ROLE_FIELD_KEY = {
    general: 'artwork_file_reference',
    cover: 'cover_file_reference',
    guts: 'interior_file_reference',
    // The collapsed cover+inside pair (config.combined_artwork = true). The two
    // slots above are hidden by that choice, so a miss must point at the single
    // slot the book step renders in their place — pointing it at a hidden field
    // would scroll the customer to nothing.
    combined: 'combined_file_reference',
    // Required only while the media packaging pick prints a wrap (packaging_art);
    // the slot itself is on every media line.
    packaging: 'packaging_file_reference',
    // Required only while custom packaging prints (customPackagingCarriesArt); the
    // slot shows only then.
    custom_packaging_art: 'custom_packaging_art_file_reference',
};
// The conditional variable-data spreadsheet requirement (role 'variable_data_list').
// UNLIKE the family roles above (unconditional, keyed off product_type), this one
// is JOB-CONFIG-conditional: turning on variable data (lineNeedsVariableDataList
// below) means the customer MUST attach the data spreadsheet before the order can
// be placed. It lives here — in the shared validator — so Save, the price
// pre-flight, the backend price gate, AND the submit gate all judge it identically
// (ADR-011: price and submit never disagree). The upload field the customer uploads
// through, so a miss scrolls straight to the inline slot.
const VARIABLE_DATA_LIST_ROLE = 'variable_data_list';
const VARIABLE_DATA_LIST_FIELD_KEY = 'variable_data_list_file_reference';
// Whether a line's variable data needs the customer's spreadsheet. It does
// whenever variable data is on, except on a Labels mail merge where the customer
// asked us to build or clean the mailing list: they may have no list to send.
// The slot's required marker, the price gates and the submit gate all read this,
// so none of them can ask for a list another one waived.
export function lineNeedsVariableDataList(config) {
    const record = configRecord(config);
    if (record.variable_print !== true)
        return false;
    return !(record.product_type === 'labels' &&
        record.mail_merge === true &&
        record.mail_merge_list_source === 'need_help');
}
// The disc/drive content requirement (ADR-021 §7). Content lives in the media
// content store, not in Asset rows, so it is not a role: the caller passes the
// line's delivery answer and whether that delivery is satisfied
// (mediaContentLineReady over the line's latest session / link check). A miss
// points at the control that delivery reveals.
const MEDIA_CONTENT_MISSING_FIELD_KEY = {
    upload: 'media_content_file_reference',
    link: 'media_content_link',
};
// The file_reference field keys a single line still needs an upload for, given
// the quote-wide set of roles that already have ≥1 non-deleted Asset. Pools
// roles quote-wide (one cover on the quote satisfies cover for every line) to
// match getQuoteRoleStatus / the submit gate exactly. Returns [] when:
//   - no product_type is chosen yet (the config validator already flags that —
//     we don't pile an upload demand on a half-started line),
//   - the family's required roles are all present (or a `combined` PDF stands in
//     for cover + guts, or a self-cover booklet needs no separate cover —
//     validateRoleSet's escape hatches), or
//   - a missing role has no customer upload field (defensive; never happens for
//     the three required roles above).
//
// `selfCover` is the line's `config.self_cover` flag — passed through to
// validateRoleSet so a self-cover booklet (cover slot hidden) isn't asked for a
// cover file. Pass the SAME flag the submit gate reads, or price/submit diverge.
//
// `artworkPendingRoles` is the line's art-to-follow slots
// (linePendingArtworkRoles). Each one waives THAT slot's family requirement and
// no other — a ticked cover leaves the manuscript required. Pass the SAME set on
// every surface (SPA pre-flight, backend price gate, submit gate) or price/submit
// diverge. It waives ARTWORK ONLY: the variable-data spreadsheet requirement
// below is job DATA (the records to merge), not artwork, and stays enforced.
export function lineMissingUploadFieldKeys(productType, presentRoles, opts) {
    if (!productType)
        return [];
    const keys = [];
    const { missing } = validateRoleSet(productType, presentRoles, {
        self_cover: opts?.selfCover === true,
        combined_artwork: opts?.combinedArtwork === true,
        packaging_art: opts?.packagingArt === true,
        custom_packaging_art: opts?.customPackagingArt === true,
    });
    for (const role of missing) {
        if (opts?.artworkPendingRoles?.has(role))
            continue;
        const fieldKey = REQUIRED_UPLOAD_ROLE_FIELD_KEY[role];
        if (fieldKey)
            keys.push(fieldKey);
    }
    // Conditional variable-data spreadsheet: required once this line turns on
    // variable data, satisfied quote-wide (one spreadsheet covers the quote, so we
    // read the pooled presentRoles). Mirrors assertVariableDataListReady's submit
    // gate, now on the price/save axis too so no surface disagrees.
    if (opts?.needsVariableDataList === true &&
        !presentRoles.has(VARIABLE_DATA_LIST_ROLE)) {
        keys.push(VARIABLE_DATA_LIST_FIELD_KEY);
    }
    const media = opts?.mediaContent;
    if (productType === 'media_replication' && media && !media.ready && media.delivery) {
        const fieldKey = MEDIA_CONTENT_MISSING_FIELD_KEY[media.delivery];
        if (fieldKey)
            keys.push(fieldKey);
    }
    return keys;
}
// True when a booklet's artwork arrived as a raw bundle in the Hands-on Prep
// catch-all — a Word doc, an InDesign package, cover + guts in one PDF — that
// prepress splits / preps / converts by hand. An UPLOAD fact the portal reports
// as a trigger; the dollar amount stays in the Nexus catalog.
//
// Art that has not arrived is NOT judged here. Whether a line whose slots are
// marked "I don't have my final artwork yet" takes Hands-on Prep is Nexus's call,
// made from the per-slot `artwork_status` the line carries (lineArtworkStatus):
// prep when every slot is pending, none when only some are. Folding that in here
// too would charge it twice.
//
// A book whose customer ticked `combined_artwork` and uploaded that one PDF is
// print-ready — the pair arrives together, not as a pile to sort — so the
// collapsed pair never triggers it. The same role ('combined') dropped WITHOUT
// the toggle is the catch-all and does.
//
// Reads `lineRoles` — the files on THIS line, plus any file attached to no line
// at all — never the rest of the quote-wide pool: the bundle is a fact about the
// line it was dropped on, and a book whose cover and guts are both in hand takes
// no prep from a neighbour's Word doc. An unattached file (an older upload, or
// one sent without a line) cannot be placed, so it counts for every line. False
// for a half-started line.
export function lineNeedsHandsOnPrep(productType, lineRoles, opts) {
    if (!productType)
        return false;
    if (opts?.combinedArtwork === true && COMBINED_ARTWORK_FAMILIES.has(productType))
        return false;
    return validateRoleSet(productType, lineRoles, { self_cover: opts?.selfCover === true })
        .accepted_via_combined;
}
// The families the seed offers the `combined_artwork` toggle on (customer-form.ts
// `coverFamilies`): books with a separate printed cover to combine. loose_leaf is
// a booklet with no printed cover, so a stale flag on it earns no waiver.
const COMBINED_ARTWORK_FAMILIES = new Set([
    'saddle_stitch_book',
    'perfect_bound_book',
    'hard_bound_book',
    'coil_or_wire_o_book',
]);
// A line's `requested_services[]` — the per-line service triggers derived from
// out-of-band state the ERP cannot see in `config`, which today means the quote's
// artwork state and nothing else.
//
// One author, both feeds. The pricing request and `/quote-detail` each put this
// array on every line, and two spellings of one rule is where a copy drifts —
// the symptom is a charge Nexus attaches on import and not on price, or the
// reverse. Takes the raw config so both callers hand it what they already hold
// (R7: one expander per axis, two callers).
export function lineRequestedServices(config, lineRoles, unscopedRoles = new Set()) {
    const record = configRecord(config);
    const productType = readLineProductType(config);
    // A raw bundle in this line's combined slot (or unattached to any line) for
    // prepress to split by hand. Art still to follow is not a trigger here — it
    // crosses as the line's artwork_status and Nexus decides. It fires per line,
    // so three bundled books send three projects each carrying it.
    return lineNeedsHandsOnPrep(productType, new Set([...lineRoles, ...unscopedRoles]), {
        selfCover: record.self_cover === true,
        combinedArtwork: record.combined_artwork === true,
    })
        ? ['combined_file_prep']
        : [];
}
//# sourceMappingURL=upload-validation.js.map